import { Injectable } from '@angular/core';
import { ActionBackOfficeResponse } from '../../models/api.model';
import {
  IdNameDto,
  ParkingFeeGroupListRow,
  ParkingFeeGroupTableDto,
  ParkingFeeListRow,
  ParkingFeeTableDto,
  ParkingRateOption,
  ParkingSubscriptionListRow,
  ParkingSubscriptionTableDto,
  SaveParkingFeeGroupPayload,
  SaveParkingFeePayload,
  SaveParkingSubscriptionPayload,
} from '../../models/finance/master-fee.model';

@Injectable({ providedIn: 'root' })
export class MapperMasterFeeService {
  mapFeeTable(dto: ParkingFeeTableDto) {
    return this.mapPagedTable(dto, (row) => this.mapFeeRow(row));
  }

  mapFeeGroupTable(dto: ParkingFeeGroupTableDto) {
    return this.mapPagedTable(dto, (row) => this.mapFeeGroupRow(row));
  }

  mapSubscriptionTable(dto: ParkingSubscriptionTableDto) {
    return this.mapPagedTable(dto, (row) => this.mapSubscriptionRow(row));
  }

  mapFeeRow(row: ParkingFeeListRow & Record<string, unknown>): ParkingFeeListRow {
    return {
      id: Number(row.id ?? row['Id'] ?? 0),
      name: String(row.name ?? row['Name'] ?? ''),
      vat: Boolean(row.vat ?? row['Vat'] ?? false),
      whTax: Number(row.whTax ?? row['WhTax'] ?? 0),
      vatIncluded: Boolean(row.vatIncluded ?? row['VatIncluded'] ?? false),
      fee: Number(row.fee ?? row['Fee'] ?? 0),
      feeGroupId: Number(row.feeGroupId ?? row['FeeGroupId'] ?? 0),
      feeGroupName: String(row.feeGroupName ?? row['FeeGroupName'] ?? ''),
      createDate: (row.createDate ?? row['CreateDate'] ?? null) as string | null,
      writeDate: (row.writeDate ?? row['WriteDate'] ?? null) as string | null,
    };
  }

  mapFeeGroupRow(row: ParkingFeeGroupListRow & Record<string, unknown>): ParkingFeeGroupListRow {
    return {
      id: Number(row.id ?? row['Id'] ?? 0),
      name: String(row.name ?? row['Name'] ?? ''),
      createDate: (row.createDate ?? row['CreateDate'] ?? null) as string | null,
      writeDate: (row.writeDate ?? row['WriteDate'] ?? null) as string | null,
    };
  }

  mapSubscriptionRow(row: ParkingSubscriptionListRow & Record<string, unknown>): ParkingSubscriptionListRow {
    return {
      id: Number(row.id ?? row['Id'] ?? 0),
      name: String(row.name ?? row['Name'] ?? ''),
      fee: Number(row.fee ?? row['Fee'] ?? 0),
      feeId: row.feeId != null ? Number(row.feeId ?? row['FeeId']) : null,
      feeName: String(row.feeName ?? row['FeeName'] ?? ''),
      siteId: row.siteId != null ? Number(row.siteId ?? row['SiteId']) : null,
      siteName: String(row.siteName ?? row['SiteName'] ?? ''),
      rateId: row.rateId != null ? Number(row.rateId ?? row['RateId']) : null,
      rateName: String(row.rateName ?? row['RateName'] ?? ''),
      remark: String(row.remark ?? row['Remark'] ?? ''),
      gateGroupId: row.gateGroupId != null ? Number(row.gateGroupId ?? row['GateGroupId']) : null,
      gateGroupName: String(row.gateGroupName ?? row['GateGroupName'] ?? ''),
      isOnline: Boolean(row.isOnline ?? row['IsOnline'] ?? false),
      isFreeParking: Boolean(row.isFreeParking ?? row['IsFreeParking'] ?? false),
      vehicleTypeId: this.toNullableNumber(row.vehicleTypeId ?? row['VehicleTypeId']),
      vehicleTypeName: String(row.vehicleTypeName ?? row['VehicleTypeName'] ?? ''),
      identify: String(row.identify ?? row['Identify'] ?? ''),
      calculateCategory: this.toNullableNumber(row.calculateCategory ?? row['CalculateCategory']),
      calculateType: this.toNullableNumber(row.calculateType ?? row['CalculateType']),
      createDate: (row.createDate ?? row['CreateDate'] ?? null) as string | null,
      writeDate: (row.writeDate ?? row['WriteDate'] ?? null) as string | null,
    };
  }

  mapIdNameList(rows: IdNameDto[] | null | undefined): IdNameDto[] {
    return (rows ?? []).map((row) => this.mapIdName(row as IdNameDto & Record<string, unknown>));
  }

  mapIdName(row: IdNameDto & Record<string, unknown>): IdNameDto {
    return {
      id: Number(row.id ?? row['Id'] ?? 0),
      name: String(row.name ?? row['Name'] ?? ''),
    };
  }

  mapRateOptions(rows: ParkingRateOption[] | null | undefined): IdNameDto[] {
    return (rows ?? []).map((row) => {
      const raw = row as unknown as Record<string, unknown>;
      return {
        id: Number(raw['id'] ?? raw['Id'] ?? 0),
        name: String(raw['name'] ?? raw['Name'] ?? ''),
      };
    });
  }

  mapFeePayload(
    detail: ParkingFeeListRow,
    userId: number,
    menuId: number
  ): SaveParkingFeePayload {
    return {
      userId,
      menuId,
      name: detail.name,
      vat: detail.vat,
      whTax: detail.whTax,
      vatIncluded: detail.vatIncluded,
      fee: detail.fee,
      feeGroupId: detail.feeGroupId > 0 ? detail.feeGroupId : null,
    };
  }

  mapFeeGroupPayload(
    detail: ParkingFeeGroupListRow,
    userId: number,
    menuId: number
  ): SaveParkingFeeGroupPayload {
    return {
      userId,
      menuId,
      name: detail.name,
    };
  }

  mapSubscriptionPayload(
    detail: ParkingSubscriptionListRow,
    userId: number,
    menuId: number,
    siteId: number
  ): SaveParkingSubscriptionPayload {
    return {
      userId,
      menuId,
      name: detail.name,
      fee: detail.fee,
      feeId: detail.feeId,
      siteId,
      rateId: detail.rateId,
      remark: detail.remark || null,
      gateGroupId: detail.gateGroupId,
      isOnline: detail.isOnline,
      isFreeParking: detail.isFreeParking,
      vehicleTypeId: detail.vehicleTypeId,
      identify: detail.identify?.trim() || null,
      calculateCategory: detail.calculateCategory,
      calculateType: detail.calculateType,
    };
  }

  createEmptyFee(): ParkingFeeListRow {
    return {
      id: 0,
      name: '',
      vat: false,
      whTax: 0,
      vatIncluded: false,
      fee: 0,
      feeGroupId: 0,
    };
  }

  createEmptyFeeGroup(): ParkingFeeGroupListRow {
    return { id: 0, name: '' };
  }

  createEmptySubscription(siteId: number): ParkingSubscriptionListRow {
    return {
      id: 0,
      name: '',
      fee: 0,
      feeId: null,
      feeName: '',
      siteId,
      siteName: '',
      rateId: null,
      rateName: '',
      remark: '',
      gateGroupId: null,
      gateGroupName: '',
      isOnline: false,
      isFreeParking: false,
      vehicleTypeId: null,
      vehicleTypeName: '',
      identify: '',
      calculateCategory: null,
      calculateType: null,
    };
  }

  mapActionResponse(res: ActionBackOfficeResponse) {
    const raw = res as unknown as Record<string, unknown>;
    return {
      isSuccess: Boolean(res.isSuccess ?? raw['IsSuccess'] ?? false),
      message: String(res.message ?? raw['Message'] ?? ''),
      messageEn: String(res.messageEn ?? raw['MessageEn'] ?? ''),
      idReturn: Number(res.IdReturn ?? raw['idReturn'] ?? raw['IdReturn'] ?? 0),
    };
  }

  private toNullableNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }

  private mapPagedTable<T>(
    dto: PagedTableResponseLike<T>,
    mapRow: (row: T & Record<string, unknown>) => T
  ) {
    const raw = dto as PagedTableResponseLike<T> & Record<string, unknown>;
    const rows = (dto.data ?? raw['Data'] ?? []) as Array<T & Record<string, unknown>>;
    const total = Number(dto.total ?? raw['Total'] ?? 0);
    const pageSize = Number(dto.pageSize ?? raw['PageSize'] ?? 20);
    return {
      items: rows.map(mapRow),
      total,
      page: Number(dto.page ?? raw['Page'] ?? 1),
      pageSize,
      totalPages: Number(dto.totalPages ?? raw['TotalPages'] ?? Math.max(1, Math.ceil(total / pageSize))),
    };
  }
}

interface PagedTableResponseLike<T> {
  data?: T[];
  total?: number;
  page?: number;
  pageSize?: number;
  totalPages?: number;
}
